import {defaultRangeExtractor, type Range, useWindowVirtualizer, type VirtualizerView} from '@github-ui/virtualizer'
import {useCallback, useMemo} from 'react'

import {useCurrentLineHeight} from '../../../../../hooks/use-current-line-height'

interface VirtualCodeBlobOptions {
  lineCount: number
  materializeAllLines: boolean
}

type VirtualCodeBlobResult = VirtualizerView<Window, HTMLElement> & {
  currentLineHeight: number
}

const defaultOverscanAmount = 100
const defaultHeadAndTailLength = 75

function defaultMeasureElement(element: Element): number {
  return element.getBoundingClientRect().height
}

export function useVirtualCodeBlob({lineCount, materializeAllLines}: VirtualCodeBlobOptions): VirtualCodeBlobResult {
  const currentLineHeight = useCurrentLineHeight('react-line-numbers')
  const estimateSize = useCallback(() => currentLineHeight, [currentLineHeight])
  const rangeExtractor = useMemo(() => getRangeExtractor(lineCount), [lineCount])

  const virtualizer = useWindowVirtualizer<HTMLElement>({
    count: lineCount,
    overscan: materializeAllLines ? Number.MAX_SAFE_INTEGER : defaultOverscanAmount,
    estimateSize,
    rangeExtractor,
    measureElement: defaultMeasureElement,
    scrollToFn,
  })

  return {...virtualizer, currentLineHeight}
}

function scrollToFn(offset: number) {
  // Use 'instant' to prevent overshoot/bounce when scrolling in virtualized view.
  // Smooth scrolling can fight with virtualization re-rendering and measurement.
  window.scrollTo({top: offset, left: 0, behavior: 'instant'})
}

export function getRangeExtractor(lineCount: number): (range: Range) => number[] {
  return function customRangeExtractor(range: Range) {
    if (lineCount < 2 * defaultHeadAndTailLength) {
      // Render all lines if the total length of the head and tail
      // together would be longer than the length of the file.
      // Uses a for-loop instead of Array(n).fill(null).map() to avoid the
      // extra .fill() pass and throwaway null array that .map() iterates over.
      const all = new Array(lineCount)
      for (let i = 0; i < lineCount; i++) all[i] = i
      return all
    }

    const indicesInWindow = defaultRangeExtractor(range)
    if (indicesInWindow.length === 0) {
      // This happens sometimes on the first render pass.
      // Just let the virtualization library do its thing on the next render.
      return indicesInWindow
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const minIndexInWindow = indicesInWindow[0]!
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const maxIndexInWindow = indicesInWindow[indicesInWindow.length - 1]!

    const headLength = Math.min(defaultHeadAndTailLength, minIndexInWindow)
    const tailLength = Math.min(defaultHeadAndTailLength, lineCount - maxIndexInWindow)
    const tailStart = lineCount + 1 - tailLength

    // Pre-allocate a single result array for [head, window, tail] instead of
    // creating three separate arrays and concatenating them. This runs on every
    // virtualization frame during scroll, so avoiding the extra allocations
    // reduces GC pressure.
    const result = new Array(headLength + indicesInWindow.length + (lineCount - tailStart))
    let idx = 0

    // Head: always-materialized first N lines (e.g. for anchor links)
    for (let i = 0; i < headLength; i++) result[idx++] = i

    // Window: lines currently visible in the viewport (from tanstack default extractor)
    for (let i = 0; i < indicesInWindow.length; i++) result[idx++] = indicesInWindow[i]

    // Tail: always-materialized last N lines (e.g. for anchor links)
    for (let i = tailStart; i < lineCount; i++) result[idx++] = i

    return result
  }
}
